import { fetchDeletedMeetingList, fetchMeetingList } from '@/services/meetings'

/** How many titles to pull when scanning for the day's existing runs. */
const SCAN_LIMIT = 100

/**
 * Format a date as the `YYYYMMDD` stamp used in Teams-seeded meeting names.
 *
 * Uses the LOCAL date deliberately: this is the day the user sees next to the
 * meeting in their Teams calendar, so a UTC stamp would read as "yesterday"
 * for anyone west of Greenwich in the evening.
 *
 * @param date Moment the meeting is being started.
 */
export function formatMeetingNameDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

/**
 * Build the `<title>_<YYYYMMDD>_` prefix that every run of one meeting on one
 * day shares. Also what gets sent as the server-side `search` term.
 *
 * @param teamsTitle Calendar title reported by Teams, e.g. `[BrSE] Daily Meeting`.
 * @param date Moment the meeting is being started.
 */
export function buildMeetingNamePrefix(teamsTitle: string, date: Date): string {
  return `${teamsTitle.trim()}_${formatMeetingNameDate(date)}_`
}

function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Pick the run number for a new meeting, given the titles already saved.
 *
 * Takes `max + 1` rather than `count + 1` so a gap cannot hand its number to
 * a new meeting: with `_2` missing, counting would resurrect `_2` while `_3`
 * still exists.
 *
 * Pass the deleted runs in too. A soft-deleted meeting keeps its title — the
 * server refuses a name it is using — so leaving it out moves the highest run
 * back onto a number that is still taken.
 *
 * @param prefix Output of {@link buildMeetingNamePrefix}.
 * @param existingTitles Saved meeting titles to scan; unrelated ones are ignored.
 * @returns One-based run number, `1` when nothing matches yet.
 * @example
 * nextMeetingNameSequence('Daily_20260819_', ['Daily_20260819_1']) // 2
 */
export function nextMeetingNameSequence(
  prefix: string,
  existingTitles: readonly string[]
): number {
  // Anchored and escaped: a title like `[BrSE] Daily Meeting` is full of
  // regex metacharacters, and `contains` matching on the server side also
  // returns near-misses such as `Daily_20260819_1 (copy)`.
  const pattern = new RegExp(`^${escapeForRegExp(prefix)}(\\d+)$`)
  let highest = 0
  for (const title of existingTitles) {
    const matched = pattern.exec(title.trim())
    if (!matched) continue
    highest = Math.max(highest, Number(matched[1]))
  }
  return highest + 1
}

/**
 * Compose the full Teams-seeded name from its parts.
 *
 * @param teamsTitle Calendar title reported by Teams.
 * @param date Moment the meeting is being started.
 * @param existingTitles Saved meeting titles to scan for the day's runs.
 * @example
 * buildTeamsMeetingName('[BrSE] Daily Meeting', new Date(2026, 7, 19), [])
 * // '[BrSE] Daily Meeting_20260819_1'
 */
export function buildTeamsMeetingName(
  teamsTitle: string,
  date: Date,
  existingTitles: readonly string[]
): string {
  const prefix = buildMeetingNamePrefix(teamsTitle, date)
  return `${prefix}${nextMeetingNameSequence(prefix, existingTitles)}`
}

/**
 * Resolve the default name for a meeting started from the Teams side panel:
 * `<Teams title>_<YYYYMMDD>_<run>`.
 *
 * The run number is derived from titles already on the server rather than a
 * local counter, so a second browser tab or a second device still sees the
 * runs the first one saved. Two tabs racing on the same number is still
 * possible; `assertMeetingTitleAvailable` + the 409 on submit stay the final
 * guard, exactly as they are for `Meeting#N`.
 *
 * Both tabs are scanned, because the trash holds its titles: a run deleted
 * this morning would otherwise be offered again this afternoon and refused.
 *
 * Falls back to `<prefix>1` when the lookup fails, so the panel never blocks
 * on a list request.
 *
 * @param teamsTitle Calendar title reported by Teams. Must be non-empty.
 * @param now Moment the meeting is being started; injectable for tests.
 * @returns A default meeting name candidate (subject to 409 on submit).
 */
export async function resolveTeamsMeetingName(
  teamsTitle: string,
  now: Date = new Date()
): Promise<string> {
  const prefix = buildMeetingNamePrefix(teamsTitle, now)
  try {
    const [active, deleted] = await Promise.all([
      fetchMeetingList({ search: prefix, limit: SCAN_LIMIT }),
      fetchDeletedMeetingList({ search: prefix, limit: SCAN_LIMIT }),
    ])
    const titles = [...active.items, ...deleted.items].map((item) => item.title)
    return `${prefix}${nextMeetingNameSequence(prefix, titles)}`
  } catch {
    return `${prefix}1`
  }
}
